import React, { useEffect, useRef, useState } from 'react';
import { GradientTextAction, InputLabel, Poppins, Spacer, StyledAvatar, UserProfileModalSkeleton } from 'src/common';
import { Guard } from '../Guard';
import { useAuth } from 'src/state/auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getProfileCredentials } from 'src/utils/auth';
import { handleImageChange } from './util';
import MaterialInput from 'src/components/form/MaterialInput';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { GenericSelect } from 'src/components/select/GenericSelect';
import { formatDate } from 'src/utils/misc';
import { Modal } from '../comps';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AuditSection } from './sections';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { useCache } from 'src/graphql/useCache';
import { Role, useUpdateUserMutation, useFlatOrganisationsQuery, useUserQuery } from 'src/graphql/_generated';
import useInitialValues from './useInitialValues';
import { OrgAccess } from './OrgAccess';
import { SelectedOrgUnit } from './types';
import { RoleSelect } from 'src/components/select';

const ModalWrap = styled(Modal)`
  width: 1350px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .p-head {
    display: flex;
    align-items: center;
    grid-gap: 10px;
  }

  .img-input {
    display: none;
  }

  form {
    .contents {
      display: grid;
      grid-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .btm-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;

    &--left {
      text-align: left;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-gap: 20px;
      }
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    form {
      .contents {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const RoleHighlight = styled.div<{ $active: boolean }>`
  position: relative;
  border-radius: 6px;

  ${({ $active, theme }) =>
    $active &&
    `
    &::before {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 8px;
      border: 2px solid ${theme.colors.gp1};
      animation: roleHighlightPulse 1.5s ease-in-out infinite;
    }

    @keyframes roleHighlightPulse {
      0%, 100% {
        opacity: 0.4;
        box-shadow: 0 0 8px ${theme.colors.gp1}40;
      }
      50% {
        opacity: 1;
        box-shadow: 0 0 16px ${theme.colors.gp1}80;
      }
    }
  `}
`;

const validationSchema = Yup.object({
  name: Yup.string().required('Required').max(100, 'Max 100 characters'),
});

interface UserModalProps {
  onClose: () => void;
  onUserUpdated?: () => void;
  externalUserId?: number;
  workspaceId?: string;
  orgId?: string;
  highlightRoles?: boolean;
}

export const UserModal: React.FC<UserModalProps> = ({
  onClose,
  externalUserId,
  workspaceId,
  orgId,
  onUserUpdated,
  highlightRoles,
}) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<SelectedOrgUnit | undefined>(undefined);
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [err, setErr] = useState('');
  const { profile, isPlatformAdmin, features, isPartnerAdmin } = useAuth();
  const { refetchQuery } = useCache();
  const [unitNotFound, setUnitNotFound] = useState(false);

  const userId = (externalUserId || Number(profile?.user.id)) as number;
  const isOwnProfile = userId === Number(profile?.user.id);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: userData, loading: userLoading } = useUserQuery({
    variables: { id: userId.toString() },
    skip: !profile?.user?.id,
  });

  const targetUserEmail = userData?.user?.email || null;

  const shouldSkipFlatOrgs = !isOwnProfile && !targetUserEmail;

  const { data: flatOrganisationsData, loading: flatOrgsLoading } = useFlatOrganisationsQuery({
    variables: { memberEmail: targetUserEmail },
    skip: shouldSkipFlatOrgs,
  });

  const loading = userLoading || flatOrgsLoading || shouldSkipFlatOrgs;

  useEffect(() => {
    if (!loading && flatOrganisationsData?.flatOrganisations) {
      const id = workspaceId || orgId;

      if (id) {
        const unit = flatOrganisationsData.flatOrganisations.find((o) => o.id === id);
        setSelectedOrgUnit(unit);
        setUnitNotFound(!unit);
      }
    }
  }, [workspaceId, orgId, flatOrganisationsData, loading]);

  const { initError, initialValues } = useInitialValues({
    selectedOrgUnit,
    isReady: !loading,
  });

  const [updateUser] = useUpdateUserMutation({
    onCompleted: () => {
      refetchQuery({ include: ['profile', 'usersEmails'] });
      resetForm({ values });
      onUserUpdated?.();
      setIsCloseRequested(true);
    },
    onError: (err) => setErr(err?.message || 'Something went wrong'),
  });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    isValid,
    isSubmitting,
    dirty,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      setErr('');
      if (!selectedOrgUnit) {
        return updateUser({
          variables: {
            userId,
            name: values.name,
            receiveNotifications: !!values.receiveNotifications,
          },
        });
      }

      return updateUser({
        variables: {
          orgUnitId: selectedOrgUnit.id,
          userId,
          name: values.name,
          role: values.role,
          receiveNotifications: !!values.receiveNotifications,
        },
      });
    },
  });

  const memberActions = selectedOrgUnit?.member?.memberActions;
  const canEditUser = isPlatformAdmin || memberActions?.canEditProfile;

  const getPermissionBasedDisabledOptions = (recordType: 'O' | 'W'): { value: Role; reason: string }[] => {
    const disabledOptions: { value: Role; reason: string }[] = [];
    const insufficientPermissionsReason = 'Insufficent permissions. Contact your admin for access.';

    if (recordType === 'O') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.OrganisationAdmin, reason: insufficientPermissionsReason });
      }
      if (!memberActions?.canMakePartnerAdmin) {
        disabledOptions.push({
          value: Role.PartnerBranchAdmin,
          reason: isPartnerAdmin
            ? 'To assign this user as a Partner Branch Admin, you must first clear their existing permissions. Alternatively, you can invite a new user profile using an alias: name_surname+admin@domain.com'
            : insufficientPermissionsReason,
        });
      }
    } else if (recordType === 'W') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.WorkspaceAdmin, reason: insufficientPermissionsReason });
      }
    }

    return disabledOptions;
  };

  const notifications = () => {
    const canEdit = isOwnProfile || canEditUser;
    return (
      <>
        <Spacer $px={20} />
        <InputLabel>DAILY DIGEST NOTIFICATIONS</InputLabel>
        <GenericSelect
          disabled={!canEdit}
          onChange={(value) => {
            const newValue = value.value === 'true';
            setFieldValue('receiveNotifications', newValue);
            mpEvent(MPEvents.NotificationToggle, { enabled: newValue });
          }}
          options={[
            { label: 'On', value: 'true' },
            { label: 'Off', value: 'false' },
          ]}
          value={values.receiveNotifications ? 'true' : 'false'}
        />
      </>
    );
  };

  const isWorkspace = selectedOrgUnit?.recordType === 'W';

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <ModalWrap onRequestClose={() => setIsCloseRequested(true)} isOpen center={false}>
          <Spacer $px={40} />
          <div className="h-padding">
            {!loading ? (
              <>
                {unitNotFound ? (
                  <Poppins color="error" px={14}>
                    User not found
                  </Poppins>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="p-head">
                      <Poppins px={18} weight={600}>
                        {values?.defaultName?.toUpperCase()}
                      </Poppins>
                      <Poppins weight={500} px={14} color="cflowerBlue">
                        for
                      </Poppins>
                      <div css="flex:1;display: grid; max-width: 420px;margin-right: 30px;">
                        <OrgAccess
                          flatOrganisationsData={flatOrganisationsData}
                          loading={loading}
                          isOwnProfile={isOwnProfile}
                          selectedOrgUnit={selectedOrgUnit}
                          onChange={(selected) => {
                            setSelectedOrgUnit(selected ?? undefined);
                          }}
                          closeModal={onClose}
                        />
                      </div>
                    </div>
                    <Spacer $px={20} />
                    <StyledAvatar src={values.profilePhotoPath || selectedImage} $large>
                      {getProfileCredentials(initialValues?.name)}
                    </StyledAvatar>
                    <Spacer $px={13} />
                    <GradientTextAction onClick={() => alert('Not implemented')} px={14} weight={500}>
                      Update Photo
                    </GradientTextAction>
                    <input
                      className="img-input"
                      ref={inputRef}
                      type="file"
                      onChange={(e) => handleImageChange(e, setSelectedImage)}
                      accept="image/*"
                    />
                    <Spacer $px={10} />
                    <Poppins weight={500} px={14} color="cflowerBlue">
                      You are currently viewing {isOwnProfile ? 'your' : "user's"} profile details and activity
                      {selectedOrgUnit ? (
                        <>
                          {' '}
                          within{' '}
                          <Poppins px={14} weight={600}>
                            {selectedOrgUnit.name}
                            <Poppins weight={500} px={14} color="cflowerBlue">
                              {isWorkspace ? ' workspace' : ' organisation'}. To update{' '}
                              {isWorkspace ? 'organisation-level' : 'workspace-level'} details, select the{' '}
                              {isWorkspace ? 'organisation' : 'workspace'} from the dropdown above
                            </Poppins>
                          </Poppins>
                        </>
                      ) : (
                        ' across all your organisations and workspaces'
                      )}
                      .
                    </Poppins>

                    <Spacer $px={32} />
                    {initError ? (
                      <>
                        <Spacer $px={32} />
                        <div className="error error--left">{initError}</div>
                      </>
                    ) : (
                      <>
                        {selectedOrgUnit?.recordType === 'O' && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                                disabled={!canEditUser}
                              />
                              <Spacer $px={20} />
                              <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
                              <RoleHighlight $active={!!highlightRoles}>
                                <RoleSelect
                                  disabled={!canEditUser}
                                  onChange={(value) => setFieldValue('role', value.value)}
                                  value={values.role}
                                  disabledOptions={[
                                    {
                                      value: Role.WorkspaceAdmin,
                                      reason: `This is a workspace-level role. Switch to a workspace using the dropdown above to assign this role to the user.`,
                                    },
                                    ...getPermissionBasedDisabledOptions('O'),
                                  ]}
                                  filteredOutRoles={[Role.CustomerManager]}
                                  optionTooltips={[
                                    {
                                      value: Role.NoAccess,
                                      reason:
                                        "When setting a users' permission to blocked the user will no longer be able to access the organisation",
                                    },
                                  ]}
                                />
                              </RoleHighlight>
                            </div>
                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                            </div>
                          </div>
                        )}
                        {selectedOrgUnit?.recordType === 'W' && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                                disabled={!canEditUser}
                              />
                              <Spacer $px={20} />
                              <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
                              <RoleHighlight $active={!!highlightRoles}>
                                <RoleSelect
                                  disabled={!canEditUser}
                                  onChange={(value) => setFieldValue('role', value.value)}
                                  value={values.role}
                                  disabledOptions={[
                                    {
                                      value: Role.PartnerBranchAdmin,
                                      reason:
                                        'This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.',
                                    },
                                    {
                                      value: Role.OrganisationAdmin,
                                      reason: `This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.`,
                                    },
                                    ...getPermissionBasedDisabledOptions('W'),
                                  ]}
                                  filteredOutRoles={[Role.CustomerManager]}
                                  optionTooltips={[
                                    {
                                      value: Role.NoAccess,
                                      reason:
                                        "When setting a users' permission to blocked the user will no longer be able to access the workspace",
                                    },
                                  ]}
                                />
                              </RoleHighlight>
                              {features['app.notifications.scheduler'] && notifications()}
                            </div>

                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                              <Spacer $px={20} />
                              <InputLabel>JOIN DATE</InputLabel>
                              <MaterialInput value={formatDate(values.acceptedDate)} fullWidth disabled />
                            </div>
                          </div>
                        )}
                        {!selectedOrgUnit && (
                          <div className="contents">
                            <div>
                              <InputLabel>NAME</InputLabel>
                              <AutoGrowingInput
                                id="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                data-cy="name-input"
                              />
                              {features['app.notifications.scheduler'] && notifications()}
                            </div>
                            <div>
                              <InputLabel>EMAIL ADDRESS</InputLabel>
                              <MaterialInput value={values?.email} fullWidth disabled />
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {err && (
                      <>
                        <Spacer $px={20} />
                        <div className="error error--left">{err}</div>
                      </>
                    )}
                    <Spacer $px={50} />
                    <div className="btm-controls">
                      <Button
                        primary
                        type="submit"
                        disabled={!isValid || isSubmitting || !dirty}
                        data-cy="update-button"
                        $constWidth={220}
                        onClick={() =>
                          mpEvent(MPEvents.ButtonClick, {
                            button: 'Update',
                            value: userId,
                            tags: ['USER'],
                          })
                        }
                      >
                        UPDATE
                      </Button>
                    </div>
                    {!!selectedOrgUnit?.recordType || isOwnProfile ? (
                      <AuditSection userId={userId} selectedOrgUnit={selectedOrgUnit} />
                    ) : (
                      <Spacer $px={20} />
                    )}
                  </form>
                )}
              </>
            ) : (
              <>
                <UserProfileModalSkeleton />
                <Spacer $px={20} />
              </>
            )}
          </div>
        </ModalWrap>
      }
    />
  );
};
